import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./multi.carousal.style.css";

/**
 * MultiCarousal – a custom circular carousel that always fills the viewport.
 *
 * Behaviour per item count:
 *   ≥ 3 items  → circular rotation, always fills the viewport
 *   < 3 items  → static layout on large screens, no rotation
 *
 * Props:
 *   speed   – autoplay interval in ms (default 4000)
 *   section – aria-label (passthrough)
 */
function MultiCarousal({ children, ...props }) {
  const items = React.Children.toArray(children);
  const totalItems = items.length;

  /* ── responsive breakpoints ─────────────────── */
  const getItemsPerView = useCallback(() => {
    const w = window.innerWidth;
    if (w >= 1500) return 4;
    if (w >= 890)  return 3;
    if (w >= 576)  return 2;
    return 1;
  }, []);

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView);

  useEffect(() => {
    const handleResize = () => setItemsPerView(getItemsPerView());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getItemsPerView]);

  /* ── rotation state ─────────────────────────── */
  const [offset, setOffset] = useState(0);
  const [animate, setAnimate] = useState(false);
  const intervalRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);

  const shouldRotate = totalItems >= 3;

  /* ── duplicated items for seamless loop ─────── */
  // We create enough copies so we can always slide without hitting the end.
  // For infinite loop we need enough buffer on both sides.
  const loopedItems = useMemo(() => {
    if (!shouldRotate || totalItems === 0) return items;
    // Use enough copies so we have itemsPerView + totalItems buffer on each side
    const copies = Math.max(3, Math.ceil((itemsPerView + totalItems * 2) / totalItems));
    return Array.from({ length: copies }, () => items).flat();
  }, [items, totalItems, shouldRotate, itemsPerView]);

  /* ── derived values ─────────────────────────── */
  // When not rotating (fewer than 3 items), cap itemsPerView to totalItems
  // so items don't overlap (e.g. 2 items on a 4-per-view screen)
  const effectiveItemsPerView = shouldRotate ? itemsPerView : Math.min(itemsPerView, totalItems);
  const itemWidthPct = 100 / effectiveItemsPerView;

  // The "middle" copy start index – we start here so we can go both directions
  const middleStart = useMemo(() => {
    if (!shouldRotate) return 0;
    return Math.floor(loopedItems.length / 2 / totalItems) * totalItems;
  }, [loopedItems, totalItems, shouldRotate]);

  // The boundary where we need to jump back
  const boundaryLow = middleStart;
  const boundaryHigh = middleStart + totalItems - 1;

  /* ── initialize offset to middle ────────────── */
  useEffect(() => {
    if (shouldRotate) {
      setOffset(middleStart);
    }
  }, [shouldRotate, middleStart]);

  /* ── navigation ─────────────────────────────── */
  const goNext = useCallback(() => {
    if (!shouldRotate) return;
    setAnimate(true);
    setOffset((prev) => prev + 1);
  }, [shouldRotate]);

  const goPrev = useCallback(() => {
    if (!shouldRotate) return;
    setAnimate(true);
    setOffset((prev) => prev - 1);
  }, [shouldRotate]);

  /* ── handle transition end – jump back to middle ── */
  const handleTransitionEnd = useCallback(() => {
    setAnimate(false);
    setOffset((prev) => {
      // If we've gone past the boundary, jump back to the equivalent position
      // in the middle copy
      if (prev < boundaryLow) {
        return prev + totalItems;
      }
      if (prev > boundaryHigh) {
        return prev - totalItems;
      }
      return prev;
    });
  }, [boundaryLow, boundaryHigh, totalItems]);

  /* ── autoplay ───────────────────────────────── */
  const startAutoplay = useCallback(() => {
    stopAutoplay();
    intervalRef.current = setInterval(goNext, props.speed ?? 4000);
  }, [goNext, props.speed]);

  const stopAutoplay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (shouldRotate) startAutoplay();
    return stopAutoplay;
  }, [shouldRotate, startAutoplay]);

  /* ── touch / swipe ──────────────────────────── */
  const handleTouchStart = (e) => {
    if (!shouldRotate) return;
    stopAutoplay();
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
  };

  const handleTouchMove = (e) => {
    if (!shouldRotate) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (!isSwiping.current && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      isSwiping.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (!shouldRotate || !isSwiping.current) {
      startAutoplay();
      return;
    }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      dx > 0 ? goPrev() : goNext();
    }
    startAutoplay();
  };

  /* ── mouse wheel ────────────────────────────── */
  const handleWheel = useCallback((e) => {
    if (!shouldRotate) return;
    stopAutoplay();
    if (e.deltaX > 20) goNext();
    else if (e.deltaX < -20) goPrev();
    startAutoplay();
  }, [shouldRotate, goNext, goPrev, startAutoplay]);

  /* ── render ─────────────────────────────────── */
  const displayItems = shouldRotate ? loopedItems : items;

  return (
    <div className="w-100">
      <div
        className={`custom-carousel-wrapper ${!shouldRotate ? "static-mode" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        onMouseEnter={stopAutoplay}
        onMouseLeave={startAutoplay}
        role="region"
        aria-label={props.section || "Advertisement carousel"}
      >
        <div
          className="custom-carousel-track"
          style={{
            transform: `translateX(-${offset * itemWidthPct}%)`,
            transition: animate ? "transform 600ms ease-in-out" : "none",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {displayItems.map((child, idx) => (
            <div
              key={idx}
              className="custom-carousel-item"
              style={{ flex: `0 0 ${itemWidthPct}%`, maxWidth: `${itemWidthPct}%` }}
            >
              {child}
            </div>
          ))}
        </div>

        {/* Navigation arrows – only when rotating */}
        {shouldRotate && (
          <>
            <button
              className="custom-carousel-arrow custom-carousel-arrow--left flaticon-back-5"
              onClick={() => { stopAutoplay(); goPrev(); startAutoplay(); }}
              aria-label="Previous advertisement"
            />
            <button
              className="custom-carousel-arrow custom-carousel-arrow--right flaticon-next-7"
              onClick={() => { stopAutoplay(); goNext(); startAutoplay(); }}
              aria-label="Next advertisement"
            />
          </>
        )}
      </div>
    </div>
  );
}

export default MultiCarousal;
